/**
 * Utility functions to extract social media handles from various input formats
 * Handles full URLs, handles with @, and plain handles
 */

/**
 * Extract LinkedIn username from URL or return cleaned handle
 * Returns the path format: "in/username" or "company/companyname"
 * Handles formats:
 * - https://www.linkedin.com/in/username -> "in/username"
 * - https://linkedin.com/company/companyname -> "company/companyname"
 * - username -> "in/username" (assumes personal profile)
 */
export const cleanLinkedInHandle = (input) => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL
  if (trimmed.includes('linkedin.com')) {
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const pathParts = url.pathname.split('/').filter(part => part);
      
      // LinkedIn URLs are typically /in/username or /company/companyname
      if (pathParts.length >= 2 && (pathParts[0] === 'in' || pathParts[0] === 'company')) {
        return `${pathParts[0]}/${pathParts[1]}`; // Return "in/username" or "company/name"
      }
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = trimmed.match(/linkedin\.com\/(in|company)\/([^/?]+)/);
      if (match) return `${match[1]}/${match[2]}`; // Return "in/username" or "company/name"
    }
  }
  
  // For plain usernames, assume personal profile (/in/).
  // Strip any prefixes that are already present so typing a character at a time
  // can't stack them into "in/in/in/username".
  let cleaned = trimmed.replace(/^@/, '').replace(/\/$/, '');
  let prefix = 'in';
  let match = cleaned.match(/^(in|company)\/(.*)$/);
  while (match) {
    prefix = match[1];
    cleaned = match[2];
    match = cleaned.match(/^(in|company)\/(.*)$/);
  }
  return cleaned ? `${prefix}/${cleaned}` : '';
};

/**
 * Extract X/Twitter username from URL or return cleaned handle
 * Handles formats:
 * - https://x.com/username
 * - https://twitter.com/username
 * - @username
 * - username
 */
export const cleanXHandle = (input) => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL
  if (trimmed.includes('x.com') || trimmed.includes('twitter.com')) {
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const pathParts = url.pathname.split('/').filter(part => part);
      
      if (pathParts.length >= 1) {
        return pathParts[0].replace(/^@/, '');
      }
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = trimmed.match(/(?:x\.com|twitter\.com)\/([^/?]+)/);
      if (match) return match[1].replace(/^@/, '');
    }
  }
  
  // Remove @ symbol if present and any trailing slashes
  return trimmed.replace(/^@/, '').replace(/\/$/, '');
};

/**
 * Extract Facebook username from URL or return cleaned handle
 * Handles formats:
 * - https://www.facebook.com/username
 * - https://facebook.com/pages/pagename/id
 * - username
 */
export const cleanFacebookHandle = (input) => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL
  if (trimmed.includes('facebook.com')) {
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const pathParts = url.pathname.split('/').filter(part => part);
      
      if (pathParts.length >= 1 && pathParts[0] !== 'pages') {
        return pathParts[0];
      }
      // For pages, use the page name
      if (pathParts[0] === 'pages' && pathParts.length >= 2) {
        return pathParts[1];
      }
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = trimmed.match(/facebook\.com\/([^/?]+)/);
      if (match) return match[1];
    }
  }
  
  // Remove @ symbol if present and any trailing slashes
  return trimmed.replace(/^@/, '').replace(/\/$/, '');
};

/**
 * Extract Instagram username from URL or return cleaned handle
 * Handles formats:
 * - https://www.instagram.com/username
 * - https://instagram.com/username/
 * - @username
 * - username
 */
export const cleanInstagramHandle = (input) => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL
  if (trimmed.includes('instagram.com')) {
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const pathParts = url.pathname.split('/').filter(part => part);
      
      if (pathParts.length >= 1) {
        return pathParts[0].replace(/^@/, '');
      }
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = trimmed.match(/instagram\.com\/([^/?]+)/);
      if (match) return match[1].replace(/^@/, '');
    }
  }
  
  // Remove @ symbol if present and any trailing slashes
  return trimmed.replace(/^@/, '').replace(/\/$/, '');
};

/**
 * Extract YouTube channel name from URL or return cleaned handle
 * Handles formats:
 * - https://www.youtube.com/@channelname
 * - https://youtube.com/c/channelname
 * - https://youtube.com/channel/channelid
 * - channelname
 */
export const cleanYouTubeHandle = (input) => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL
  if (trimmed.includes('youtube.com')) {
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      const pathParts = url.pathname.split('/').filter(part => part);
      
      if (pathParts.length >= 1) {
        // Handle @channelname format
        if (pathParts[0].startsWith('@')) {
          return pathParts[0];
        }
        // Handle /c/channelname or /channel/channelid
        if (pathParts.length >= 2 && (pathParts[0] === 'c' || pathParts[0] === 'channel')) {
          return pathParts[1];
        }
        return pathParts[0];
      }
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = trimmed.match(/youtube\.com\/(?:@|c\/|channel\/)([^/?]+)/);
      if (match) return match[1];
    }
  }
  
  // Return as-is (may have @ for new format) and remove trailing slashes
  return trimmed.replace(/\/$/, '');
};
